import _slicedToArray from "/home/circleci/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import _defineProperty from "/home/circleci/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/defineProperty";
import _toConsumableArray from "/home/circleci/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/toConsumableArray";
import _objectWithoutProperties from "/home/circleci/repo/node_modules/babel-preset-react-app/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(source, true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(source).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { useRef, useEffect, useMemo } from 'react';
import { useSpacing } from '../../api/spacing/useSpacing';
import { FormLabel } from '../FormLabel';
import { FormHelperText } from '../FormHelperText';
import { pickComponentCommonProps, pickAriaProps, pickDataProps } from '../../interfaces/commonProps';
import { useLocale } from '../LocaleProvider';
import { StyledMinMax, StyledOutput, StyledSliderContainer, StyledContainer, StyledTrack } from './Slider.styles';
import { SliderThumb } from './SliderThumb';

var noop = function noop() {};

var validateNumberOfThumbs = function validateNumberOfThumbs(numberOfThumbs) {
  return numberOfThumbs === 1 || numberOfThumbs === 2;
};

export var Slider = React.forwardRef(function (_ref, ref) {
  var _ref$ariaDescribedby = _ref['aria-describedby'],
      describedBy = _ref$ariaDescribedby === void 0 ? '' : _ref$ariaDescribedby,
      disabled = _ref.disabled,
      userDisplayFormat = _ref.displayFormat,
      id = _ref.id,
      hint = _ref.hint,
      invalid = _ref.invalid,
      label = _ref.label,
      list = _ref.list,
      max = _ref.max,
      min = _ref.min,
      _ref$onChange = _ref.onChange,
      onChange = _ref$onChange === void 0 ? noop : _ref$onChange,
      _ref$onFocus = _ref.onFocus,
      onFocus = _ref$onFocus === void 0 ? noop : _ref$onFocus,
      _ref$onBlur = _ref.onBlur,
      onBlur = _ref$onBlur === void 0 ? noop : _ref$onBlur,
      step = _ref.step,
      validationMessage = _ref.validationMessage,
      unsanitizedValue = _ref.value,
      valueFormat = _ref.valueFormat,
      children = _ref.children,
      restProps = _objectWithoutProperties(_ref, ["aria-describedby", "disabled", "displayFormat", "id", "hint", "invalid", "label", "list", "max", "min", "onChange", "onFocus", "onBlur", "step", "validationMessage", "value", "valueFormat", "children"]);

  var _useLocale = useLocale(),
      localeIdentifier = _useLocale.localeIdentifier;

  var internalValueFormat = useMemo(function () {
    return valueFormat || new Intl.NumberFormat(localeIdentifier).format;
  }, [localeIdentifier, valueFormat]);
  var valuesArray = useMemo(function () {
    return Array.isArray(unsanitizedValue) ? unsanitizedValue : [unsanitizedValue];
  }, [unsanitizedValue]);
  var numberOfThumbs = valuesArray.length;

  if (!validateNumberOfThumbs(numberOfThumbs)) {
    throw new Error("Expected to receive 1 or 2 thumbs, got ".concat(numberOfThumbs, "."));
  }

  var containerRef = useRef(null);
  var outputRef = useRef(null);
  useEffect(function () {
    if (containerRef.current) {
      containerRef.current.style.setProperty('--value0', valuesArray[0].toString());

      if (numberOfThumbs === 2) {
        containerRef.current.style.setProperty('--value1', valuesArray[1].toString());
        containerRef.current.style.setProperty('--start', "var(".concat(valuesArray[0] < valuesArray[1] ? '--position0' : '--position1', ")"));
        containerRef.current.style.setProperty('--end', "var(".concat(valuesArray[0] > valuesArray[1] ? '--position0' : '--position1', ")"));
      }

      containerRef.current.style.setProperty('--min', min.toString());
      containerRef.current.style.setProperty('--max', max.toString());
    }
  }, [containerRef, valuesArray, numberOfThumbs, min, max]);

  var injectOutputSize = function injectOutputSize() {
    if (containerRef.current && outputRef.current) {
      containerRef.current.style.setProperty('--output-size', "".concat(outputRef.current.clientWidth, "px"));
    }
  };

  useEffect(injectOutputSize, [valueFormat, containerRef, outputRef, valuesArray, min, max]);
  useEffect(function () {
    if (document.fonts) document.fonts.ready.then(injectOutputSize);
  }, []);
  var useExternalDataList = !Array.isArray(list);
  var labelId = "".concat(id, "__label");
  var dataListId = useExternalDataList ? list : "".concat(id, "__datalist");
  var hintMessageId = "".concat(id, "__hint-message");
  var validationMessageId = "".concat(id, "__invalid-message");
  var mergedDescribedBy = [hint ? hintMessageId : '', invalid ? validationMessageId : '', describedBy].join(' ').trim();
  useMemo(function () {
    if (!label && !restProps['aria-label'] && !restProps['aria-labelledby']) {
      console.warn('It appears you may be missing a label. In the absence of the "label" prop, please provide an "aria-label" or "aria-labelledby" attribute to associate a label.');
    }
  }, // eslint-disable-next-line react-hooks/exhaustive-deps
  [label, restProps['aria-label'], restProps['aria-labelledby']]);

  var thumbChange = function thumbChange(index) {
    return function (event) {
      var nextValue = parseFloat(event.target.value);

      if (numberOfThumbs === 2) {
        var arrayCopy = _toConsumableArray(valuesArray);

        arrayCopy[index] = nextValue;
        onChange(arrayCopy, event);
      } else {
        onChange(nextValue, event);
      }
    };
  };

  var locale = useLocale();

  var render = function render(propsArray) {
    return propsArray.map(function (props) {
      return React.createElement(SliderThumb, _objectSpread({
        key: props.id
      }, props));
    });
  };

  var displayFormat = userDisplayFormat || (numberOfThumbs === 2 ? function (_ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
        start = _ref3[0],
        end = _ref3[1];

    return locale.slider.selectedRange({
      start: start,
      end: end
    });
  } : function (i) {
    return i;
  });
  return React.createElement(StyledContainer, _objectSpread({
    outerSpacing: useSpacing(restProps),
    id: id,
    ref: ref,
    as: numberOfThumbs === 2 ? 'fieldset' : 'div'
  }, pickDataProps(restProps), {}, pickAriaProps(restProps), {}, pickComponentCommonProps(restProps), {
    "aria-describedby": numberOfThumbs === 2 ? mergedDescribedBy : undefined
  }), label && React.createElement(FormLabel, {
    id: labelId,
    as: numberOfThumbs === 2 ? 'legend' : 'label',
    required: true,
    spaceStackEnd: "xxs"
  }, label), !useExternalDataList && React.createElement("datalist", {
    id: dataListId
  }, list.map(function (entry) {
    var _ref4 = Array.isArray(entry) ? entry : [entry],
        _ref5 = _slicedToArray(_ref4, 2),
        value = _ref5[0],
        label = _ref5[1];

    return React.createElement("option", {
      key: value,
      value: value,
      label: label
    });
  })), React.createElement(StyledSliderContainer, {
    ref: containerRef,
    numberOfThumbs: numberOfThumbs,
    disabled: disabled,
    invalid: invalid
  }, React.createElement(StyledTrack, {
    numberOfThumbs: numberOfThumbs,
    marks: !useExternalDataList ? list.map(function (value) {
      return Array.isArray(value) ? value[0] : value;
    }) : [],
    disabled: disabled,
    invalid: invalid
  }), render(valuesArray.map(function (value, index) {
    return _objectSpread({
      'aria-valuemax': max,
      'aria-valuemin': min,
      'aria-valuenow': value,
      'aria-valuetext': internalValueFormat(value)
    }, numberOfThumbs === 1 && {
      'aria-labelledby': labelId,
      'aria-describedby': mergedDescribedBy
    }, {}, numberOfThumbs === 2 && _objectSpread({}, index === 0 && {
      'aria-label': locale.slider.startAriaLabel
    }, {}, index === 1 && {
      'aria-label': locale.slider.endAriaLabel
    }), {
      disabled: disabled,
      id: "".concat(id, "__thumb-").concat(index),
      invalid: invalid,
      list: dataListId,
      max: max,
      min: min,
      onBlur: onBlur,
      onChange: thumbChange(index),
      onFocus: onFocus,
      step: step,
      value: value
    });
  })), React.createElement(StyledOutput, {
    numberOfThumbs: numberOfThumbs,
    disabled: disabled,
    invalid: invalid,
    ref: outputRef
  }, displayFormat(_toConsumableArray(valuesArray).sort(function (a, b) {
    return a - b;
  }).map(internalValueFormat))), React.createElement(StyledMinMax, {
    "aria-hidden": true
  }, React.createElement("span", null, internalValueFormat(min)), React.createElement("span", null, internalValueFormat(max)))), hint && React.createElement(FormHelperText, {
    id: hintMessageId,
    spaceStackStart: 's',
    variant: "hint"
  }, hint), invalid && React.createElement(FormHelperText, {
    id: validationMessageId,
    spaceStackStart: hint ? 'xxs' : 's',
    variant: "invalid"
  }, validationMessage));
});
Slider.displayName = 'Slider';